import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, CalendarDays, CalendarRange, Settings2, Check, ArrowRight } from "lucide-react";

const TEMPO_OPTIONS = [
  {
    id: "one_big_day",
    label: "One Big Day",
    desc: "Plan everything for your actual birthday. One epic day.",
    icon: Calendar,
    accent: "from-amber-500 to-orange-500",
    lightBg: "bg-amber-50",
    lightBorder: "border-amber-200",
    lightText: "text-amber-700",
  },
  {
    id: "birthday_week",
    label: "Birthday Week",
    desc: "Spread it across 7 days. No rush, more fun.",
    icon: CalendarDays,
    accent: "from-sky-500 to-blue-500",
    lightBg: "bg-sky-50",
    lightBorder: "border-sky-200",
    lightText: "text-sky-700",
  },
  {
    id: "birthday_month",
    label: "Birthday Month",
    desc: "Grab things when convenient. Your whole birthday month.",
    icon: CalendarRange,
    accent: "from-violet-500 to-purple-500",
    lightBg: "bg-violet-50",
    lightBorder: "border-violet-200",
    lightText: "text-violet-700",
  },
  {
    id: "custom",
    label: "Custom",
    desc: "Pick your available days. I'll build around your schedule.",
    icon: Settings2,
    accent: "from-emerald-500 to-teal-500",
    lightBg: "bg-emerald-50",
    lightBorder: "border-emerald-200",
    lightText: "text-emerald-700",
  },
];

export default function TempoSelector({ userColor, birthday, onSelect }) {
  const [selected, setSelected] = useState(null);
  const [customDates, setCustomDates] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const color = userColor || "#0ea5e9";

  const handleSelect = (tempoId) => {
    setSelected(tempoId);
    if (tempoId === "custom") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
    }
  };

  const handleConfirm = () => {
    if (!selected) return;
    if (selected === "custom" && customDates.length === 0) return;
    onSelect({ tempo: selected, customDates: selected === "custom" ? customDates : [] });
  };

  const toggleDate = (dateStr) => {
    setCustomDates(prev =>
      prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr].sort()
    );
  };

  // Generate next 45 days for custom picker
  const getDateRange = () => {
    const dates = [];
    const start = new Date();
    for (let i = 0; i < 45; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const canConfirm = selected && (selected !== "custom" || customDates.length > 0);

  return (
    <div className="px-4 pt-6 pb-8" data-testid="tempo-selector">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Header */}
        <div className="text-center space-y-2">
          <h2 className="text-xl font-black text-slate-800">
            How do you want to enjoy your birthday season?
          </h2>
          <p className="text-sm text-slate-500">
            Choose your collection tempo. We'll build a plan that fits your pace.
          </p>
        </div>

        {/* 2x2 Grid */}
        <div className="grid grid-cols-2 gap-3" data-testid="tempo-grid">
          {TEMPO_OPTIONS.map((tempo, idx) => {
            const Icon = tempo.icon;
            const isSelected = selected === tempo.id;
            return (
              <motion.button
                key={tempo.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.08 }}
                onClick={() => handleSelect(tempo.id)}
                className={`relative p-4 rounded-2xl text-left transition-all duration-200 ${
                  isSelected
                    ? "ring-2 shadow-lg scale-[1.02]"
                    : "border border-slate-200 hover:border-slate-300 hover:shadow-sm"
                }`}
                style={isSelected ? {
                  borderColor: color,
                  ringColor: color,
                  backgroundColor: `${color}08`,
                  boxShadow: `0 4px 20px ${color}20`,
                  '--tw-ring-color': color,
                } : {}}
                data-testid={`tempo-${tempo.id}`}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: color }}
                  >
                    <Check className="w-3 h-3 text-white" />
                  </motion.div>
                )}

                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                    isSelected ? "" : tempo.lightBg
                  }`}
                  style={isSelected ? { backgroundColor: `${color}18` } : {}}
                >
                  <Icon
                    className="w-5 h-5"
                    style={isSelected ? { color } : {}}
                  />
                </div>
                <p className={`text-sm font-bold mb-1 ${
                  isSelected ? "text-slate-900" : "text-slate-700"
                }`}>
                  {tempo.label}
                </p>
                <p className="text-[11px] text-slate-500 leading-snug">
                  {tempo.desc}
                </p>
              </motion.button>
            );
          })}
        </div>

        {/* Custom Date Picker */}
        <AnimatePresence>
          {showDatePicker && selected === "custom" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
              data-testid="custom-date-picker"
            >
              <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-slate-700">
                    Pick your available days
                  </p>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}15`, color }}>
                    {customDates.length} selected
                  </span>
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {/* Day headers */}
                  {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                    <div key={i} className="text-center text-[9px] font-bold text-slate-400 py-1">{d}</div>
                  ))}
                  {/* Offset for first day of week */}
                  {(() => {
                    const dates = getDateRange();
                    const firstDay = dates[0].getDay();
                    const blanks = Array(firstDay).fill(null);
                    return [...blanks, ...dates].map((date, i) => {
                      if (!date) return <div key={`blank-${i}`} />;
                      const dateStr = date.toISOString().split("T")[0];
                      const isChosen = customDates.includes(dateStr);
                      const isBirthday = birthday && dateStr === birthday;
                      const dayNum = date.getDate();
                      return (
                        <button
                          key={dateStr}
                          onClick={() => toggleDate(dateStr)}
                          className={`py-1.5 rounded-lg text-xs font-medium transition-all ${
                            isChosen
                              ? "text-white shadow-sm"
                              : isBirthday
                                ? "bg-pink-50 text-pink-600 font-bold ring-1 ring-pink-300"
                                : "text-slate-600 hover:bg-slate-100"
                          }`}
                          style={isChosen ? { backgroundColor: color } : {}}
                          data-testid={`date-${dateStr}`}
                        >
                          {dayNum}
                        </button>
                      );
                    });
                  })()}
                </div>
                {customDates.length > 0 && (
                  <p className="text-[10px] text-slate-400 text-center">
                    {customDates.length} day{customDates.length !== 1 ? "s" : ""} selected — stops will be distributed across these days
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Build My Plan Button */}
        <motion.button
          onClick={handleConfirm}
          disabled={!canConfirm}
          className={`w-full py-4 rounded-2xl text-white text-sm font-bold flex items-center justify-center gap-2 transition-all ${
            canConfirm ? "shadow-lg hover:shadow-xl" : "opacity-40 cursor-not-allowed"
          }`}
          style={{ backgroundColor: canConfirm ? color : "#94a3b8" }}
          whileTap={canConfirm ? { scale: 0.98 } : {}}
          data-testid="tempo-confirm-btn"
        >
          Build My Plan <ArrowRight className="w-4 h-4" />
        </motion.button>
      </motion.div>
    </div>
  );
}
